import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  InputAdornment,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Avatar,
  Stack,
  Autocomplete,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  List as ListIcon,
  AddCircle as AddCircleIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { baseUrl, config, requestID } from '../../../common/utils';
import { IonPage } from '@ionic/react';

const InvoiceSuspenseManager = () => {
  // State management
  const [suspenses, setSuspenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  
  // Services state
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  
  // Customers state for autocomplete
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOpen, setCustomerOpen] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Dialog states
  const [addDialog, setAddDialog] = useState({
    open: false,
    loading: false,
    formData: {
      invoice_id: '',
      customer_id: '',
      customer_name: '',
      service_id: '',
      service_amount: '',
    },
    errors: {},
  });

  const [serviceDialog, setServiceDialog] = useState({
    open: false,
    loading: false,
    mode: 'add', // 'add' or 'edit'
    formData: {
      id: '',
      service_id: '',
      service_name: '',
    },
    errors: {},
  });

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    suspense: null,
    loading: false,
  });

  const [serviceDeleteDialog, setServiceDeleteDialog] = useState({
    open: false,
    service: null,
    loading: false,
  });

  const [detailsDialog, setDetailsDialog] = useState({
    open: false,
    suspense: null,
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    resolved: 0,
    totalAmount: 0,
  });

  // Fetch data on mount
  useEffect(() => {
    fetchSuspenses();
    fetchServices();
  }, []);

  // Fetch customers when search term changes
  useEffect(() => {
    if (customerSearchTerm.length < 3) {
      setCustomers([]);
      setCustomerOpen(false);
      return;
    }

    setCustomersLoading(true);
    const handler = setTimeout(() => {
      fetchCustomers(customerSearchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [customerSearchTerm]);

  // Calculate stats when suspenses change
  useEffect(() => {
    if (suspenses.length > 0) {
      const total = suspenses.length;
      const pending = suspenses.filter(s => s.status === 'pending').length;
      const resolved = suspenses.filter(s => s.status === 'resolved').length;
      const totalAmount = suspenses.reduce((sum, s) => sum + parseFloat(s.service_amount || 0), 0);

      setStats({
        total,
        pending,
        resolved,
        totalAmount: totalAmount.toFixed(2),
      });
    }
  }, [suspenses]);

  // Fetch suspenses
  const fetchSuspenses = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('requestID', requestID());

      const response = await axios.post(
        `${baseUrl()}Geacloud_InvoiceSuspenses/getAllInvoiceSuspense`,
        formData,
        config
      );

      if (response.data.status) {
        setSuspenses(response.data.data || []);
      } else {
        showSnackbar(response.data.message, 'error');
      }
    } catch (error) {
      console.error('Error fetching suspenses:', error);
      showSnackbar('Failed to fetch suspenses', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch services
  const fetchServices = async () => {
    setServicesLoading(true);
    try {
      const formData = new FormData();
      formData.append('requestID', requestID());

      const response = await axios.post(
        `${baseUrl()}Geacloud_Services/getServices`,
        formData,
        config
      );

      if (response.data.status) {
        setServices(response.data.data || []);
      } else {
        showSnackbar(response.data.message, 'error');
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      showSnackbar('Failed to fetch services', 'error');
    } finally {
      setServicesLoading(false);
    }
  };

  // Fetch customers
  const fetchCustomers = async (searchQuery) => {
    try {
      const formData = new FormData();
      formData.append('requestID', requestID());
      formData.append('search', searchQuery);

      const response = await axios.post(
        `${baseUrl()}users/getCustomers2wallet_only`,
        formData,
        config
      );

      setCustomers(response.data.data || []);
      setCustomerOpen(true);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    } finally {
      setCustomersLoading(false);
    }
  };

  // Handle form changes for suspense
  const handleFormChange = (field, value) => {
    setAddDialog(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [field]: value,
      },
      errors: {
        ...prev.errors,
        [field]: '',
      },
    }));
  };

  // Handle form changes for service
  const handleServiceFormChange = (field, value) => {
    setServiceDialog(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [field]: value,
      },
      errors: {
        ...prev.errors,
        [field]: '',
      },
    }));
  };

  // Validate suspense form
  const validateForm = () => {
    const errors = {};
    const { invoice_id, customer_id, service_id, service_amount } = addDialog.formData;

    if (!invoice_id.trim()) errors.invoice_id = 'Invoice ID is required';
    if (!customer_id.trim()) errors.customer_id = 'Customer is required';
    if (!service_id.trim()) errors.service_id = 'Service is required';
    if (!service_amount.trim()) errors.service_amount = 'Amount is required';
    if (isNaN(service_amount) || parseFloat(service_amount) <= 0) {
      errors.service_amount = 'Amount must be a positive number';
    }

    return errors;
  };

  // Validate service form
  const validateServiceForm = () => {
    const errors = {};
    const { service_id, service_name } = serviceDialog.formData;

    if (!service_id.trim()) errors.service_id = 'Service ID is required';
    if (!service_name.trim()) errors.service_name = 'Service Name is required';

    return errors;
  };

  // Add new suspense
  const handleAddSuspense = async () => {
    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setAddDialog(prev => ({ ...prev, errors }));
      return;
    }

    setAddDialog(prev => ({ ...prev, loading: true }));

    try {
      const formData = new FormData();
      formData.append('requestID', requestID());
      formData.append('invoice_id', addDialog.formData.invoice_id);
      formData.append('customer_id', addDialog.formData.customer_id);
      formData.append('service_id', addDialog.formData.service_id);
      formData.append('service_amount', addDialog.formData.service_amount);

      const response = await axios.post(
        `${baseUrl()}Geacloud_InvoiceSuspenses/addInvoiceSuspense`,
        formData,
        config
      );

      if (response.data.status) {
        showSnackbar('Suspense added successfully', 'success');
        fetchSuspenses();
        handleCloseAddDialog();
      } else {
        showSnackbar(response.data.message, 'error');
      }
    } catch (error) {
      console.error('Error adding suspense:', error);
      showSnackbar('Failed to add suspense', 'error');
    } finally {
      setAddDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Add/Edit service
  const handleSaveService = async () => {
    const errors = validateServiceForm();
    
    if (Object.keys(errors).length > 0) {
      setServiceDialog(prev => ({ ...prev, errors }));
      return;
    }

    setServiceDialog(prev => ({ ...prev, loading: true }));

    try {
      const formData = new FormData();
      formData.append('requestID', requestID());
      
      const endpoint = serviceDialog.mode === 'add' 
        ? 'Geacloud_Services/addService' 
        : 'Geacloud_Services/updateService';
      
      if (serviceDialog.mode === 'edit') {
        formData.append('id', serviceDialog.formData.id);
      }
      
      formData.append('service_id', serviceDialog.formData.service_id);
      formData.append('service_name', serviceDialog.formData.service_name);

      const response = await axios.post(
        `${baseUrl()}${endpoint}`,
        formData,
        config
      );

      if (response.data.status) {
        showSnackbar(`Service ${serviceDialog.mode === 'add' ? 'added' : 'updated'} successfully`, 'success');
        fetchServices();
        handleCloseServiceDialog();
      } else {
        showSnackbar(response.data.message, 'error');
      }
    } catch (error) {
      console.error('Error saving service:', error);
      showSnackbar('Failed to save service', 'error');
    } finally {
      setServiceDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Delete suspense
  const handleDeleteSuspense = async () => {
    if (!deleteDialog.suspense) return;

    setDeleteDialog(prev => ({ ...prev, loading: true }));

    try {
      const formData = new FormData();
      formData.append('requestID', requestID());
      formData.append('suspense_id', deleteDialog.suspense.id);

      const response = await axios.post(
        `${baseUrl()}InvoiceGeacloud_suspenses/deleteInvoiceSuspense`,
        formData,
        config
      );

      if (response.data.status) {
        showSnackbar('Suspense deleted successfully', 'success');
        fetchSuspenses();
        setDeleteDialog({ open: false, suspense: null, loading: false });
      } else {
        showSnackbar(response.data.message, 'error');
      }
    } catch (error) {
      console.error('Error deleting suspense:', error);
      showSnackbar('Failed to delete suspense', 'error');
    } finally {
      setDeleteDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Delete service
  const handleDeleteService = async () => {
    if (!serviceDeleteDialog.service) return;

    setServiceDeleteDialog(prev => ({ ...prev, loading: true }));

    try {
      const formData = new FormData();
      formData.append('requestID', requestID());
      formData.append('id', serviceDeleteDialog.service.id);

      const response = await axios.post(
        `${baseUrl()}Geacloud_Services/deleteService`,
        formData,
        config
      );

      if (response.data.status) {
        showSnackbar('Service deleted successfully', 'success');
        fetchServices();
        setServiceDeleteDialog({ open: false, service: null, loading: false });
      } else {
        showSnackbar(response.data.message, 'error');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      showSnackbar('Failed to delete service', 'error');
    } finally {
      setServiceDeleteDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Filter suspenses
  const filteredSuspenses = suspenses.filter(suspense => {
    if (searchTerm && !suspense.invoice_id?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    if (statusFilter !== 'all' && suspense.status !== statusFilter) {
      return false;
    }
    
    if (dateFilter !== 'all') {
      const suspenseDate = new Date(suspense.created_at);
      const now = new Date();
      const diffDays = Math.floor((now - suspenseDate) / (1000 * 60 * 60 * 24));
      
      if (dateFilter === 'today' && diffDays > 0) return false;
      if (dateFilter === 'week' && diffDays > 7) return false;
      if (dateFilter === 'month' && diffDays > 30) return false;
    }
    
    return true;
  });

  // Helper functions
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleCloseAddDialog = () => {
    setAddDialog({
      open: false,
      loading: false,
      formData: {
        invoice_id: '',
        customer_id: '',
        customer_name: '',
        service_id: '',
        service_amount: '',
      },
      errors: {},
    });
    setSelectedCustomer(null);
    setCustomerSearchTerm('');
  };

  const handleCloseServiceDialog = () => {
    setServiceDialog({
      open: false,
      loading: false,
      mode: 'add',
      formData: {
        id: '',
        service_id: '',
        service_name: '',
      },
      errors: {},
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'resolved': return 'success';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <PendingIcon />;
      case 'resolved': return <CheckCircleIcon />;
      default: return null;
    }
  };

  return (
    <IonPage>
    <Container maxWidth="xl" sx={{ py: 4, bgcolor: 'white' }}>
      {/* Header with Tabs */}
      <Box sx={{ mb: 4, bgcolor: 'white' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <ReceiptIcon fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight={700} color="primary">
                Invoice Suspense Management
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage pending invoice suspense transactions and services
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{ 
              '& .MuiTab-root': { 
                py: 2,
                fontSize: '0.9rem',
                fontWeight: 600 
              }
            }}
          >
            <Tab 
              icon={<ReceiptIcon />} 
              iconPosition="start" 
              label="Suspense Management" 
            />
            <Tab 
              icon={<ListIcon />} 
              iconPosition="start" 
              label="Services Management" 
            />
          </Tabs>
        </Paper>

        {/* Stats Cards */}
        {activeTab === 0 && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                p: 2, 
                height: '100%',
                bgcolor: 'white',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 2,
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': { 
                  boxShadow: 4,
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h2" fontWeight={700} color="primary">
                        {stats.total}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Suspenses
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'primary.light' }}>
                      <ReceiptIcon sx={{ color: 'primary.main' }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                p: 2, 
                height: '100%',
                bgcolor: 'white',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 2,
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': { 
                  boxShadow: 4,
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h2" fontWeight={700} color="warning.main">
                        {stats.pending}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pending
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'warning.light' }}>
                      <PendingIcon sx={{ color: 'warning.main' }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                p: 2, 
                height: '100%',
                bgcolor: 'white',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 2,
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': { 
                  boxShadow: 4,
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h2" fontWeight={700} color="success.main">
                        {stats.resolved}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Resolved
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'success.light' }}>
                      <CheckCircleIcon sx={{ color: 'success.main' }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                p: 2, 
                height: '100%',
                bgcolor: 'white',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 2,
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': { 
                  boxShadow: 4,
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h3" fontWeight={700} color="info.main">
                        {formatCurrency(stats.totalAmount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Amount
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'info.light' }}>
                      <MoneyIcon sx={{ color: 'info.main' }} />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Content based on active tab */}
      {activeTab === 0 ? (
        <>
          {/* Search and Filters for Suspense */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2, bgcolor: 'white' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search by Invoice ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="primary" />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 1 }
                  }}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{ borderRadius: 1 }}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateFilter}
                    label="Date Range"
                    onChange={(e) => setDateFilter(e.target.value)}
                    sx={{ borderRadius: 1 }}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">Last 7 Days</MenuItem>
                    <MenuItem value="month">Last 30 Days</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialog(prev => ({ ...prev, open: true }))}
                sx={{ borderRadius: 1 }}
              >
                Add New Suspense
              </Button>
            </Box>
          </Paper>

          {/* Suspense Table */}
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 3, bgcolor: 'white' }}>
            {loading && <LinearProgress />}
            
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={600}>
                Invoice Suspenses ({filteredSuspenses.length})
              </Typography>
              
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchSuspenses}
                disabled={loading}
                variant="outlined"
                size="small"
                sx={{ borderRadius: 1 }}
              >
                Refresh
              </Button>
            </Box>

            <Divider />

            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>INVOICE</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>CUSTOMER</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>SERVICE</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">AMOUNT</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>STATUS</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>CREATED</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSuspenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <PendingIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                        <Typography color="textSecondary" variant="h6" gutterBottom>
                          No suspenses found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm || statusFilter !== 'all' || dateFilter !== 'all'
                            ? 'Try adjusting your filters'
                            : 'Add your first suspense using the "Add Suspense" button'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSuspenses.map((suspense) => (
                      <TableRow 
                        key={suspense.id} 
                        hover
                        sx={{ 
                          '&:hover': { bgcolor: 'action.hover' },
                          cursor: 'pointer'
                        }}
                        onClick={() => setDetailsDialog({ open: true, suspense })}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                              <ReceiptIcon fontSize="small" sx={{ color: 'white' }} />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                INV-{suspense.invoice_id}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {suspense.id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              CUST-{suspense.customer_id}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2">
                            {services.find(s => s.service_id === suspense.service_id)?.service_name || `SRV-${suspense.service_id}`}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600} color="primary">
                            {formatCurrency(suspense.service_amount)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(suspense.status)}
                            label={suspense.status.toUpperCase()}
                            color={getStatusColor(suspense.status)}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(suspense.created_at)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDetailsDialog({ open: true, suspense });
                                }}
                              >
                                <BusinessIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteDialog({ open: true, suspense, loading: false });
                                }}
                                disabled={suspense.status !== 'pending'}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      ) : (
        <>
          {/* Services Management */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2, bgcolor: 'white' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={600}>
                Services Management
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setServiceDialog({ 
                  open: true, 
                  loading: false, 
                  mode: 'add',
                  formData: { id: '', service_id: '', service_name: '' },
                  errors: {} 
                })}
                sx={{ borderRadius: 1 }}
              >
                Add New Service
              </Button>
            </Box>

            <TableContainer >
              <Table >
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>SERVICE ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>SERVICE NAME</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>CREATED</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {services.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <ListIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                        <Typography color="textSecondary" variant="h6" gutterBottom>
                          No services found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Add your first service using the "Add New Service" button
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    services.map((service) => (
                      <TableRow key={service.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {service.service_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {service.service_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(service.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center">
                            <Tooltip title="Edit Service">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => setServiceDialog({ 
                                  open: true, 
                                  loading: false, 
                                  mode: 'edit',
                                  formData: { 
                                    id: service.id, 
                                    service_id: service.service_id, 
                                    service_name: service.service_name 
                                  },
                                  errors: {} 
                                })}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="Delete Service">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setServiceDeleteDialog({ 
                                  open: true, 
                                  service, 
                                  loading: false 
                                })}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* Add/Edit Suspense Dialog */}
      <Dialog
        open={addDialog.open}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        className="pushDown"
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <AddIcon />
            <Typography variant="h6">Add New Suspense</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }} className="pushDown">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Invoice ID *"
                value={addDialog.formData.invoice_id}
                onChange={(e) => handleFormChange('invoice_id', e.target.value)}
                error={!!addDialog.errors.invoice_id}
                helperText={addDialog.errors.invoice_id}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                open={customerOpen}
                onOpen={() => {
                  if (customerSearchTerm.length >= 3) {
                    setCustomerOpen(true);
                  }
                }}
                onClose={() => setCustomerOpen(false)}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setCustomerSearchTerm(newInputValue);
                    handleFormChange('customer_name', newInputValue);
                  }
                }}
                onChange={(event, newValue) => {
                  setSelectedCustomer(newValue);
                  if (newValue) {
                    handleFormChange('customer_id', newValue.user_id);
                    handleFormChange('customer_name', `${newValue.firstName} ${newValue.lastName || ''}`);
                  } else {
                    handleFormChange('customer_id', '');
                    handleFormChange('customer_name', '');
                  }
                }}
                options={customers}
                loading={customersLoading}
                getOptionLabel={(option) =>
                  `${option.firstName} ${option.lastName || ''} : ${option.phone}` || ""
                }
                value={selectedCustomer}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Customer *"
                    error={!!addDialog.errors.customer_id}
                    helperText={addDialog.errors.customer_id || "Type at least 3 characters to search"}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {customersLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    size="small"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.user_id}>
                    {option.firstName} {option.lastName || ''} : {option.phone}
                  </li>
                )}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Service *</InputLabel>
                <Select
                  value={addDialog.formData.service_id}
                  label="Service *"
                  onChange={(e) => handleFormChange('service_id', e.target.value)}
                  error={!!addDialog.errors.service_id}
                >
                  <MenuItem value="">Select Service</MenuItem>
                  {services.map((service) => (
                    <MenuItem key={service.id} value={service.service_id}>
                      {service.service_id} - {service.service_name}
                    </MenuItem>
                  ))}
                </Select>
                {addDialog.errors.service_id && (
                  <Typography variant="caption" color="error">
                    {addDialog.errors.service_id}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount *"
                type="number"
                value={addDialog.formData.service_amount}
                onChange={(e) => handleFormChange('service_amount', e.target.value)}
                error={!!addDialog.errors.service_amount}
                helperText={addDialog.errors.service_amount}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
          </Grid>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            Suspense will be created with "pending" status
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseAddDialog}
            variant="outlined"
            disabled={addDialog.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddSuspense}
            variant="contained"
            color="primary"
            disabled={addDialog.loading}
            startIcon={addDialog.loading ? <CircularProgress size={20} /> : null}
          >
            {addDialog.loading ? 'Adding...' : 'Add Suspense'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Service Dialog */}
      <Dialog
        open={serviceDialog.open}
        onClose={handleCloseServiceDialog}
        maxWidth="sm"
        fullWidth
      
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <Box display="flex" alignItems="center" gap={1}>
            {serviceDialog.mode === 'add' ? <AddIcon /> : <EditIcon />}
            <Typography variant="h6">
              {serviceDialog.mode === 'add' ? 'Add New Service' : 'Edit Service'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }} className="pushDown">
          <Grid container spacing={2} >
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Service ID *"
                value={serviceDialog.formData.service_id}
                onChange={(e) => handleServiceFormChange('service_id', e.target.value)}
                error={!!serviceDialog.errors.service_id}
                helperText={serviceDialog.errors.service_id}
                size="small"
                disabled={serviceDialog.mode === 'edit'}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Service Name *"
                value={serviceDialog.formData.service_name}
                onChange={(e) => handleServiceFormChange('service_name', e.target.value)}
                error={!!serviceDialog.errors.service_name}
                helperText={serviceDialog.errors.service_name}
                size="small"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseServiceDialog}
            variant="outlined"
            disabled={serviceDialog.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveService}
            variant="contained"
            color="primary"
            disabled={serviceDialog.loading}
            startIcon={serviceDialog.loading ? <CircularProgress size={20} /> : null}
          >
            {serviceDialog.loading ? 'Saving...' : serviceDialog.mode === 'add' ? 'Add Service' : 'Update Service'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Suspense Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleteDialog.loading && setDeleteDialog({ open: false, suspense: null, loading: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon />
            <Typography variant="h6">Delete Suspense</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {deleteDialog.suspense && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This action cannot be undone. Only pending suspenses can be deleted.
              </Alert>
              
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <ReceiptIcon sx={{ color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    Invoice: INV-{deleteDialog.suspense.invoice_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Customer: CUST-{deleteDialog.suspense.customer_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Amount: {formatCurrency(deleteDialog.suspense.service_amount)}
                  </Typography>
                </Box>
              </Box>
              
              {deleteDialog.suspense.status !== 'pending' && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Only pending suspenses can be deleted. This suspense is {deleteDialog.suspense.status}.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => !deleteDialog.loading && setDeleteDialog({ open: false, suspense: null, loading: false })}
            variant="outlined"
            disabled={deleteDialog.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSuspense}
            variant="contained"
            color="error"
            disabled={deleteDialog.loading || (deleteDialog.suspense?.status !== 'pending')}
            startIcon={deleteDialog.loading ? <CircularProgress size={20} /> : null}
          >
            {deleteDialog.loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Service Dialog */}
      <Dialog
        open={serviceDeleteDialog.open}
        onClose={() => !serviceDeleteDialog.loading && setServiceDeleteDialog({ open: false, service: null, loading: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon />
            <Typography variant="h6">Delete Service</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {serviceDeleteDialog.service && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This action cannot be undone. The service will be marked as inactive.
              </Alert>
              
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <ListIcon sx={{ color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    Service ID: {serviceDeleteDialog.service.service_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Service Name: {serviceDeleteDialog.service.service_name}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => !serviceDeleteDialog.loading && setServiceDeleteDialog({ open: false, service: null, loading: false })}
            variant="outlined"
            disabled={serviceDeleteDialog.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteService}
            variant="contained"
            color="error"
            disabled={serviceDeleteDialog.loading}
            startIcon={serviceDeleteDialog.loading ? <CircularProgress size={20} /> : null}
          >
            {serviceDeleteDialog.loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, suspense: null })}
        maxWidth="md"
        fullWidth
      >
        {detailsDialog.suspense && (
          <>
            <DialogTitle sx={{ bgcolor: 'info.main', color: 'white' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <BusinessIcon />
                <Typography variant="h6">Suspense Details</Typography>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      INVOICE INFORMATION
                    </Typography>
                    <Stack spacing={2}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Invoice ID:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          INV-{detailsDialog.suspense.invoice_id}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Suspense ID:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {detailsDialog.suspense.id}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Status:</Typography>
                        <Chip
                          icon={getStatusIcon(detailsDialog.suspense.status)}
                          label={detailsDialog.suspense.status.toUpperCase()}
                          color={getStatusColor(detailsDialog.suspense.status)}
                          size="small"
                        />
                      </Box>
                    </Stack>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      CUSTOMER INFORMATION
                    </Typography>
                    <Stack spacing={2}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Customer ID:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          CUST-{detailsDialog.suspense.customer_id}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Service ID:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          SRV-{detailsDialog.suspense.service_id}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Branch ID:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {detailsDialog.suspense.branch_id}
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      FINANCIAL INFORMATION
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="center" py={2}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 80, height: 80, mr: 3 }}>
                        <MoneyIcon sx={{ fontSize: 40, color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Service Amount
                        </Typography>
                        <Typography variant="h3" color="primary" fontWeight={700}>
                          {formatCurrency(detailsDialog.suspense.service_amount)}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      TIMELINE
                    </Typography>
                    <Stack spacing={2}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Created:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDate(detailsDialog.suspense.created_at)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Last Updated:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDate(detailsDialog.suspense.updated_at || detailsDialog.suspense.created_at)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setDetailsDialog({ open: false, suspense: null })}
                variant="contained"
                color="primary"
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
    </IonPage>
  );
};

export default InvoiceSuspenseManager;